import { useState } from "react";
import { Alert, Grid } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { differenceInCalendarDays, format, parseISO } from "date-fns";
import { useUser } from "../../../api/useAuth";
import { DASHBOARD_COLORS, paletteColorAt } from "../../../dashboard-framework/colors";
import { useTechnicianColors, applyTechnicianSeriesColors } from "../../../dashboard-framework/technicianColors";
import { useDashboardAccess } from "../../../dashboard-framework/DashboardAccessProvider";
import { DashboardPage } from "../../../dashboard-framework/DashboardPage";
import { resolveDateFilterRange } from "../../../dashboard-framework/datePresets";
import { formatDateRangePt, TREATMENT_STATUS_LABELS_PT } from "../../../dashboard-framework/describeWidgetLogic";
import { formatPercent, formatQuantity } from "../../../dashboard-framework/format";
import { buildPivot, PivotMeasure, PivotNode } from "../../../dashboard-framework/pivot";
import { ChartSeriesConfig, DrillDownConfig, GlobalFilterValues } from "../../../dashboard-framework/types";
import { PivotTableCard } from "../../../dashboard-framework/widgets/PivotTableCard";
import { StatCard } from "../../../dashboard-framework/widgets/StatCard";
import { TimeSeriesChart, TimeSeriesTooltipConfig } from "../../../dashboard-framework/widgets/TimeSeriesChart";
import { WidgetCard } from "../../../dashboard-framework/widgets/WidgetCard";
import { CLIENT_TYPE_LABELS, COARSE_DISORDER_GROUPS, coarseDisorderGroupLabel, coarseDisorderGroupOf, disorderDrillLabelOf } from "../shared/disorderGroups";
import { fetchAllItemPages } from "../shared/fetchAllItemPages";
import {
    CONVERSION_MAX_PAGES,
    CONVERSION_SERVICE,
    CONVERSION_SESSION_FIELDS,
    conversionCardColor,
    conversionClientTypeFromFilter,
    conversionClinicLabelOf,
    conversionGlobalFilters,
    conversionGroupKeyFromFilter,
    conversionOwnViewFilter,
    conversionRateCellSx,
} from "./clientConversionConfig";
import {
    anomalyTreatmentCount,
    buildConversionMonthlyPoints,
    consultaDayKeyOf,
    CONVERSION_COUNT_SUFFIX,
    CONVERSION_NUM_SUFFIX,
    ConversionBreakdown,
    ConversionSessionItem,
    ConversionSummary,
    conversionChartPersonGroups,
    conversionClientTypeOf,
    conversionGroupBreakdown,
    conversionMonthIndexes,
    conversionPersonNameOf,
    conversionSummaryOf,
    filterConversionItems,
    isStartBeforeConsulta,
    scopedConversionSummary,
} from "./clientConversionRows";
import { ConversionKpiCard, ConversionKpiChip } from "./components/ConversionKpiCard";

const FIVE_MINUTES = 5 * 60 * 1000;

const formatDrillDate = (iso: string | null) => (iso ? format(parseISO(iso.slice(0, 10)), "dd/MM/yyyy") : "—");
const formatCount = (value: number | null) => (value === null ? "—" : formatQuantity(value));
const formatPct = (value: number | null) => (value === null ? "—" : formatPercent(value, 1));
const treatmentStatusLabel = (statusNew: string | null | undefined) => (statusNew ? TREATMENT_STATUS_LABELS_PT[statusNew] || statusNew : "—");

const conversionMeasures = (isClientTypeFiltered: boolean): Array<PivotMeasure<ConversionSessionItem>> => {
    const baseMeasures: Array<PivotMeasure<ConversionSessionItem>> = [
        { key: "relatorios", label: "Relatórios", isNumeric: true, compute: (items) => conversionSummaryOf(items).relatorios, format: formatCount },
        { key: "iniciaram", label: "Iniciaram", isNumeric: true, compute: (items) => conversionSummaryOf(items).iniciaram, format: formatCount },
        {
            key: "conversionPct",
            label: "% conversão",
            isNumeric: true,
            compute: (items) => conversionSummaryOf(items).pct,
            format: formatPct,
            cellSx: conversionRateCellSx,
        },
    ];
    const splitMeasures: Array<PivotMeasure<ConversionSessionItem>> = [
        { key: "adults", label: "Adultos", isNumeric: true, compute: (items) => scopedConversionSummary(items, "adults").relatorios, format: formatCount },
        {
            key: "adultsPct",
            label: "% conv. adultos",
            isNumeric: true,
            compute: (items) => scopedConversionSummary(items, "adults").pct,
            format: formatPct,
            cellSx: conversionRateCellSx,
        },
        { key: "children", label: "Crianças", isNumeric: true, compute: (items) => scopedConversionSummary(items, "children").relatorios, format: formatCount },
        {
            key: "childrenPct",
            label: "% conv. crianças",
            isNumeric: true,
            compute: (items) => scopedConversionSummary(items, "children").pct,
            format: formatPct,
            cellSx: conversionRateCellSx,
        },
    ];
    const measures = isClientTypeFiltered ? baseMeasures : [...baseMeasures, ...splitMeasures];
    return measures;
};

// Pessoas por nº de consultas (Relatórios) desc; grupos de perturbação (2º nível) alfabéticos (defeito).
const byReportsDescending = (firstNode: PivotNode<ConversionSessionItem>, secondNode: PivotNode<ConversionSessionItem>) => {
    const comparison = secondNode.items.length - firstNode.items.length || firstNode.label.localeCompare(secondNode.label);
    return comparison;
};

const conversionPivotOf = (items: Array<ConversionSessionItem>, isClientTypeFiltered: boolean) => {
    const pivot = buildPivot({
        items,
        rowLevels: [conversionPersonNameOf, (item) => coarseDisorderGroupLabel(coarseDisorderGroupOf(item.treatment?.diagnose_main?.id))],
        measures: conversionMeasures(isClientTypeFiltered),
        sortRowsBy: [byReportsDescending],
    });
    return pivot;
};

const COLUMN_TITLES: Record<string, string> = {
    relatorios: "Relatórios",
    iniciaram: "Iniciaram",
    conversionPct: "% conversão",
    adults: "Adultos",
    adultsPct: "% conv. adultos",
    children: "Crianças",
    childrenPct: "% conv. crianças",
};

// Itens do nó recortados ao tipo de cliente da coluna clicada (as colunas de split só mostram o seu tipo).
const cellItemsForColumn = (node: PivotNode<ConversionSessionItem>, columnKey: string) => {
    if (columnKey === "adults" || columnKey === "adultsPct") return node.items.filter((item) => conversionClientTypeOf(item) === "adults");
    if (columnKey === "children" || columnKey === "childrenPct") return node.items.filter((item) => conversionClientTypeOf(item) === "children");
    return node.items;
};

type ConversionDrillRow = {
    id: number | string;
    treatmentId: number | string | null;
    clientNumber: string | null;
    consultaDate: string | null;
    personName: string;
    clientTypeLabel: string;
    disorderLabel: string;
    iniciou: string;
    dateOfStart: string | null;
    statusLabel: string;
    isAnomaly: boolean;
};

const conversionDrillRowOf = (item: ConversionSessionItem): ConversionDrillRow => {
    const row: ConversionDrillRow = {
        id: item.id,
        treatmentId: item.treatment?.id ?? null,
        clientNumber: item.treatment?.client?.client_id ?? null,
        consultaDate: item.start_date,
        personName: conversionPersonNameOf(item),
        clientTypeLabel: CLIENT_TYPE_LABELS[conversionClientTypeOf(item)],
        disorderLabel: disorderDrillLabelOf(item.treatment?.diagnose_main),
        iniciou: item.treatment?.date_of_start ? "Sim" : "Não",
        dateOfStart: item.treatment?.date_of_start ?? null,
        statusLabel: treatmentStatusLabel(item.treatment?.treatment_status_new),
        isAnomaly: isStartBeforeConsulta(item),
    };
    return row;
};

const conversionDrillColumns = [
    { key: "clientNumber", label: "Nº cliente" },
    { key: "consultaDate", label: "Data consulta", valueFor: (row: ConversionDrillRow) => formatDrillDate(row.consultaDate) },
    { key: "personName", label: "Psicóloga" },
    { key: "clientTypeLabel", label: "Tipo" },
    { key: "disorderLabel", label: "Perturbação" },
    { key: "iniciou", label: "Iniciou" },
    { key: "dateOfStart", label: "Data início", valueFor: (row: ConversionDrillRow) => formatDrillDate(row.dateOfStart) },
    { key: "statusLabel", label: "Estado" },
];

// Linhas de início-antes-da-consulta a vermelho (as que o cartão de anomalias sinaliza).
const anomalyRowSx = (row: ConversionDrillRow) => (row.isAnomaly ? { backgroundColor: "rgba(226, 85, 85, 0.14)" } : {});

const treatmentRowRoute = (row: { treatmentId: number | string | null }) => (row.treatmentId ? `/treatments/${row.treatmentId}/detail` : null);

const anomalyDrillColumns = [
    { key: "clientNumber", label: "Nº cliente" },
    { key: "personName", label: "Psicóloga" },
    { key: "consultaDate", label: "Data consulta", valueFor: (row: AnomalyDrillRow) => formatDrillDate(row.consultaDate) },
    { key: "dateOfStart", label: "Data início", valueFor: (row: AnomalyDrillRow) => formatDrillDate(row.dateOfStart) },
    { key: "daysDiff", label: "Dias antes", isNumeric: true },
    { key: "statusLabel", label: "Estado" },
];

type AnomalyDrillRow = {
    id: number | string;
    treatmentId: number | string;
    clientNumber: string | null;
    personName: string;
    consultaDate: string | null;
    dateOfStart: string | null;
    daysDiff: number;
    statusLabel: string;
};

// Uma linha por tratamento anómalo distinto (reconcilia com o valor do cartão por construção).
const anomalyDrillRowsOf = (items: Array<ConversionSessionItem>) => {
    const byTreatmentId = new Map<string, ConversionSessionItem>();
    items.forEach((item) => {
        if (!isStartBeforeConsulta(item) || !item.treatment) return;
        const treatmentId = String(item.treatment.id);
        if (!byTreatmentId.has(treatmentId)) byTreatmentId.set(treatmentId, item);
    });
    const rows = Array.from(byTreatmentId.values()).map((item) => {
        const consultaDay = consultaDayKeyOf(item.start_date);
        const startDay = (item.treatment!.date_of_start as string).slice(0, 10);
        const daysDiff = consultaDay ? differenceInCalendarDays(parseISO(consultaDay), parseISO(startDay)) : 0;
        const row: AnomalyDrillRow = {
            id: item.treatment!.id,
            treatmentId: item.treatment!.id,
            clientNumber: item.treatment?.client?.client_id ?? null,
            personName: conversionPersonNameOf(item),
            consultaDate: item.start_date,
            dateOfStart: item.treatment!.date_of_start,
            daysDiff,
            statusLabel: treatmentStatusLabel(item.treatment?.treatment_status_new),
        };
        return row;
    });
    return rows;
};

// Drill de um segmento dos cartões de topo (todas as consultas do segmento, modo rows).
const segmentDrillOf = (title: string, segmentItems: Array<ConversionSessionItem>): DrillDownConfig | undefined => {
    if (segmentItems.length === 0) return undefined;
    const config: DrillDownConfig = {
        kind: "rows",
        title,
        columns: conversionDrillColumns,
        rows: segmentItems.map(conversionDrillRowOf),
        defaultSortKey: "consultaDate",
        rowRoute: treatmentRowRoute,
        rowSx: anomalyRowSx,
    };
    return config;
};

const cardValueOf = (summary: ConversionSummary) => (summary.pct === null ? "—" : formatPct(summary.pct));
const cardSubtitleOf = (summary: ConversionSummary) => `${formatQuantity(summary.iniciaram)} de ${formatQuantity(summary.relatorios)}`;
const clientTypeChipsOf = (breakdown: ConversionBreakdown): Array<ConversionKpiChip> => {
    const chips: Array<ConversionKpiChip> = [
        { label: "Adulto", value: breakdown.adults.pct === null ? "—" : formatPercent(breakdown.adults.pct, 0) },
        { label: "Criança", value: breakdown.children.pct === null ? "—" : formatPercent(breakdown.children.pct, 0) },
    ];
    return chips;
};

const conversionTooltip: TimeSeriesTooltipConfig = {
    valueFormatter: (value) => formatPercent(value, 1),
    countSuffixKey: CONVERSION_COUNT_SUFFIX,
    countNumeratorSuffixKey: CONVERSION_NUM_SUFFIX,
};

const CONVERSION_Y_DOMAIN: [number, number] = [0, 100];
const CONVERSION_Y_TICKS = [0, 25, 50, 75, 100];

export const ClientConversionDashboard = () => {
    const access = useDashboardAccess();
    const userResult = useUser();
    const [filterValues, setFilterValues] = useState<GlobalFilterValues>({ period: "ultimos_12_meses", clinic: null, disorderGroup: "all", clientType: "all" });
    const setFilterValue = (filterKey: string, value: string | number | null) => {
        setFilterValues((currentValues) => ({ ...currentValues, [filterKey]: value }));
    };

    // Vista individual (viewer_rules → params.psychologistUserId): só as consultas da própria psicóloga.
    // Enquanto o $current_user não resolve, as queries ficam parqueadas (nunca mostrar tudo).
    const currentUserId: string | null = userResult.data?.id || null;
    const viewerParamValue = access.viewerParams?.psychologistUserId;
    const ownPsychologistUserId = viewerParamValue === undefined ? null : viewerParamValue === "$current_user" ? currentUserId : String(viewerParamValue);
    const isOwnView = viewerParamValue !== undefined;
    const viewerPending = isOwnView && ownPsychologistUserId === null;

    const now = new Date();
    const periodRange = resolveDateFilterRange(filterValues, "period", now);
    const groupKey = conversionGroupKeyFromFilter(filterValues.disorderGroup);
    const clientType = conversionClientTypeFromFilter(filterValues.clientType);
    const clinicId = filterValues.clinic ?? null;

    const serverFilter = {
        _and: [
            { service: { _eq: CONVERSION_SERVICE } },
            { status: { _eq: "done" } },
            ...(periodRange ? [{ start_date: { _gte: periodRange.gte } }, { start_date: { _lt: periodRange.lt } }] : []),
            ...(clinicId !== null ? [{ clinic_id: { _eq: clinicId } }] : []),
            ...(isOwnView && ownPsychologistUserId ? [conversionOwnViewFilter(ownPsychologistUserId)] : []),
        ],
    };

    const sessionsResult = useQuery({
        queryKey: [
            "client-conversion-sessions",
            CONVERSION_SESSION_FIELDS,
            periodRange?.gte ?? "sem_periodo",
            periodRange?.lt ?? "sem_periodo",
            clinicId,
            ownPsychologistUserId,
        ],
        queryFn: () => fetchAllItemPages("sessions", CONVERSION_SESSION_FIELDS, serverFilter, { maxPages: CONVERSION_MAX_PAGES }),
        staleTime: FIVE_MINUTES,
        keepPreviousData: true,
        enabled: !viewerPending,
    });

    const allItems: Array<ConversionSessionItem> = sessionsResult.data?.items || [];
    const reachedPageLimit = Boolean(sessionsResult.data?.reachedPageLimit);
    const isLoading = viewerPending || sessionsResult.isLoading;
    const isError = sessionsResult.isError;

    // Perturbação e Tipo de cliente aplicam-se client-side sobre os itens já obtidos.
    const filteredItems = filterConversionItems(allItems, { groupKey, clientType });

    const pivot = conversionPivotOf(filteredItems, clientType !== null);
    const monthIndexes = conversionMonthIndexes(periodRange, filteredItems, now);
    const personGroups = conversionChartPersonGroups(filteredItems);
    // Cada linha por psicóloga usa a cor definida na ficha do técnico (technicians.color); sem cor definida cai na paleta de reserva.
    const { technicianColorByUserId } = useTechnicianColors();
    const personSeries: Array<ChartSeriesConfig> = applyTechnicianSeriesColors(
        personGroups.map((group, index) => ({ key: group.key, label: group.name, color: paletteColorAt(index) })),
        technicianColorByUserId
    );
    const personPoints = buildConversionMonthlyPoints(monthIndexes, personGroups);
    const totalPoints = buildConversionMonthlyPoints(monthIndexes, [{ key: "total", items: filteredItems }]);
    const totalSeries: Array<ChartSeriesConfig> = [{ key: "total", label: "Total", color: DASHBOARD_COLORS.highlight, strokeWidth: 3 }];

    const anomalyCount = anomalyTreatmentCount(filteredItems);

    // Cartões de topo: repartição FIXA do período (allItems = período + clínica + âmbito da vista), independente
    // dos filtros Perturbação/Tipo de cliente — dão a fotografia completa de relance.
    const headlineBreakdown = conversionGroupBreakdown(allItems, null);
    const adultsItems = allItems.filter((item) => conversionClientTypeOf(item) === "adults");
    const childrenItems = allItems.filter((item) => conversionClientTypeOf(item) === "children");

    // Descrições (ⓘ) — manuscritas (widgets de rácio / contagem sem describe helper), com os filtros resolvidos.
    const clinicLabel = conversionClinicLabelOf(clinicId);
    const periodText = `${periodRange ? formatDateRangePt(periodRange) : "de todo o histórico"}${clinicLabel ? ` · clínica: ${clinicLabel}` : ""}`;
    const groupText = groupKey ? ` · perturbação: ${coarseDisorderGroupLabel(groupKey)}` : "";
    const clientTypeText = clientType ? ` · ${CLIENT_TYPE_LABELS[clientType]}s` : "";
    const scopeText = isOwnView ? " Mostra apenas as tuas consultas." : "";
    const filtersText = `${periodText}${groupText}${clientTypeText}.`;

    const cohortNote =
        "A conversão é atribuída ao mês e à pessoa da CONSULTA, mesmo que o tratamento comece meses depois — por isso os meses mais recentes têm % naturalmente mais baixa (clientes ainda em decisão).";
    const anomalyWarning =
        "Tratamentos com data de início ANTERIOR à consulta contam à mesma como iniciados (erros de registo) — ver o cartão \"Inícios antes da consulta\". Uma consulta repetida do mesmo cliente conta 2 relatórios mas 1 início.";

    const pivotExplanation = {
        queryDescription:
            `Consultas de avaliação realizadas (${filtersText}) por psicóloga (do técnico da consulta) e grupo de perturbação. ` +
            `Relatórios = nº de consultas; Iniciaram = tratamentos distintos que arrancaram; % conversão = Iniciaram ÷ Relatórios. ` +
            `Adultos/Crianças = nº de consultas por idade na data da consulta (>18 adulto; ≤18 ou sem data de nascimento = criança). ` +
            `Expande cada psicóloga para o detalhe por perturbação; clica numa célula para veres as consultas por trás do número.${scopeText}`,
        businessNote: cohortNote,
        warningNote: anomalyWarning,
    };
    const perPersonChartExplanation = {
        queryDescription: `% conversão mensal por psicóloga (${filtersText}) — mês da consulta. Passa o rato num ponto para veres iniciaram/relatórios.${scopeText}`,
        businessNote: cohortNote,
        warningNote: anomalyWarning,
    };
    const totalChartExplanation = {
        queryDescription: `% conversão mensal do total da clínica (${filtersText}) — inclui consultas sem técnico atribuído.`,
        businessNote: cohortNote,
        warningNote: anomalyWarning,
    };
    const anomalyExplanation = {
        queryDescription:
            `Tratamentos distintos cuja data de início é ANTERIOR ao dia da consulta de avaliação (${filtersText}). ` +
            `Contam como iniciados na taxa de conversão, mas são quase sempre erros de registo. Clica para os corrigir.${scopeText}`,
        businessNote: "O mesmo dia não conta como anomalia.",
    };

    // Cartões de topo: seguem só o Período e a Clínica (repartição fixa por tipo de cliente e perturbação).
    const headlineCardNote = "Repartição fixa do período e da clínica — NÃO segue os filtros Perturbação/Tipo de cliente.";
    const headlineCardExplanation = (segmentText: string) => {
        const explanation = {
            queryDescription:
                `Taxa de conversão (tratamentos iniciados ÷ consultas de avaliação realizadas) ${segmentText}, ${periodText}. ` +
                `Clica para veres as consultas por trás do número.${scopeText}`,
            businessNote: headlineCardNote,
            warningNote: anomalyWarning,
        };
        return explanation;
    };
    const disorderCardExplanation = (groupLabel: string) => {
        const explanation = {
            queryDescription:
                `Taxa de conversão das consultas do grupo ${groupLabel}, ${periodText}. ` +
                `Os chips mostram a conversão de adultos e crianças deste grupo. Clica para veres as consultas.${scopeText}`,
            businessNote: headlineCardNote,
            warningNote: anomalyWarning,
        };
        return explanation;
    };

    const cellDrillDown = (node: PivotNode<ConversionSessionItem>, columnKey: string): DrillDownConfig | null => {
        const cellItems = cellItemsForColumn(node, columnKey);
        if (cellItems.length === 0) return null;
        const config: DrillDownConfig = {
            kind: "rows",
            title: `${node.path.join(" › ")} — ${COLUMN_TITLES[columnKey] || "consultas"}`,
            columns: conversionDrillColumns,
            rows: cellItems.map(conversionDrillRowOf),
            defaultSortKey: "consultaDate",
            rowRoute: treatmentRowRoute,
            rowSx: anomalyRowSx,
        };
        return config;
    };

    const anomalyDrill: DrillDownConfig = {
        kind: "rows",
        title: "Inícios antes da consulta",
        columns: anomalyDrillColumns,
        rows: anomalyDrillRowsOf(filteredItems),
        defaultSortKey: "daysDiff",
        rowRoute: treatmentRowRoute,
    };

    const anomalyValue = isError ? "—" : isLoading ? undefined : formatQuantity(anomalyCount);
    const anomalyColor = isError || anomalyCount > 0 ? DASHBOARD_COLORS.cancelled : DASHBOARD_COLORS.done;

    return (
        <DashboardPage globalFilters={conversionGlobalFilters} values={filterValues} onChange={setFilterValue}>
            {reachedPageLimit && (
                <Alert severity="warning">
                    Há demasiadas consultas para o período escolhido e os números podem estar incompletos — encurta o período.
                </Alert>
            )}

            {/* Banda de destaque no topo: Total / Adultos / Crianças + um cartão por grupo de perturbação. */}
            <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                    <ConversionKpiCard
                        label="Conversão total"
                        value={cardValueOf(headlineBreakdown.total)}
                        subtitle={cardSubtitleOf(headlineBreakdown.total)}
                        color={conversionCardColor(headlineBreakdown.total.pct)}
                        explanation={headlineCardExplanation("de todas as consultas")}
                        isLoading={isLoading}
                        drillDown={segmentDrillOf("Conversão total — consultas", allItems)}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <ConversionKpiCard
                        label="Adultos"
                        value={cardValueOf(headlineBreakdown.adults)}
                        subtitle={cardSubtitleOf(headlineBreakdown.adults)}
                        color={conversionCardColor(headlineBreakdown.adults.pct)}
                        explanation={headlineCardExplanation("dos clientes com mais de 18 anos na data da consulta")}
                        isLoading={isLoading}
                        drillDown={segmentDrillOf("Conversão — Adultos", adultsItems)}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <ConversionKpiCard
                        label="Crianças"
                        value={cardValueOf(headlineBreakdown.children)}
                        subtitle={cardSubtitleOf(headlineBreakdown.children)}
                        color={conversionCardColor(headlineBreakdown.children.pct)}
                        explanation={headlineCardExplanation("dos clientes com 18 anos ou menos (ou sem data de nascimento)")}
                        isLoading={isLoading}
                        drillDown={segmentDrillOf("Conversão — Crianças", childrenItems)}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={2}>
                {COARSE_DISORDER_GROUPS.map((group) => {
                    const groupItems = allItems.filter((item) => coarseDisorderGroupOf(item.treatment?.diagnose_main?.id) === group.key);
                    const breakdown = conversionGroupBreakdown(allItems, group.key);
                    return (
                        <Grid item xs={6} sm={3} key={group.key}>
                            <ConversionKpiCard
                                label={group.label}
                                value={cardValueOf(breakdown.total)}
                                subtitle={cardSubtitleOf(breakdown.total)}
                                chips={clientTypeChipsOf(breakdown)}
                                color={conversionCardColor(breakdown.total.pct)}
                                explanation={disorderCardExplanation(group.label)}
                                isLoading={isLoading}
                                drillDown={segmentDrillOf(`Conversão — ${group.label}`, groupItems)}
                            />
                        </Grid>
                    );
                })}
            </Grid>

            {/* Ordem do Zoho: timeline total (linha inteira) → timeline por psicóloga (linha inteira) → tabela. */}
            {!isOwnView && (
                <WidgetCard title="% conversão total" explanation={totalChartExplanation} bodyHeight={360} isLoading={isLoading} isError={isError}>
                    <TimeSeriesChart
                        points={totalPoints}
                        series={totalSeries}
                        height={340}
                        showPointLabels={false}
                        tooltip={conversionTooltip}
                        yDomain={CONVERSION_Y_DOMAIN}
                        yTicks={CONVERSION_Y_TICKS}
                    />
                </WidgetCard>
            )}

            <WidgetCard title="% conversão por psicóloga" explanation={perPersonChartExplanation} bodyHeight={360} isLoading={isLoading} isError={isError}>
                <TimeSeriesChart
                    points={personPoints}
                    series={personSeries}
                    height={340}
                    showPointLabels={false}
                    tooltip={conversionTooltip}
                    yDomain={CONVERSION_Y_DOMAIN}
                    yTicks={CONVERSION_Y_TICKS}
                />
            </WidgetCard>

            <PivotTableCard
                title="Taxas de conversão"
                explanation={pivotExplanation}
                pivot={pivot}
                rowHeaderLabel="Psicóloga / Perturbação"
                maxBodyHeight={480}
                isLoading={isLoading}
                isError={isError}
                cellDrillDown={cellDrillDown}
            />

            <Grid container spacing={3}>
                <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                        label="Inícios antes da consulta"
                        value={anomalyValue}
                        color={anomalyColor}
                        explanation={anomalyExplanation}
                        isLoading={isLoading}
                        drillDown={anomalyCount > 0 ? anomalyDrill : undefined}
                    />
                </Grid>
            </Grid>
        </DashboardPage>
    );
};
